'use client';

import React, { useMemo, useState } from 'react';
import { urlFor, unwrapImage } from '@/sanity/lib/image';
import GfSubnav from '@/components/common/GfSubnav';
import GfSectionMark from '@/components/common/GfSectionMark';
import GfCta from '@/components/common/GfCta';
import BookingLinkButton from '@/components/accommodation/BookingLinkButton';
import StayLinkButton from '@/components/accommodation/StayLinkButton';
import HotelCard from '@/components/accommodation/HotelCard';
import CityHotelsMap from '@/components/accommodation/CityHotelsMap';
import { t } from '@/i18n/translations';

const PRICE_TIERS = [
    { key: 'budget', label: '€' },
    { key: 'mid', label: '€€' },
    { key: 'upper', label: '€€€' },
    { key: 'luxury', label: '€€€€' },
];

// Удобства, по которым имеет смысл фильтровать (приоритет = порядок).
// Чип показываем, только если им обладает ЧАСТЬ отелей (не все и не никто) —
// иначе он ничего не отсекает. См. amenityFacets ниже. Лейблы — в i18n (vp.amenity).
const FILTER_AMENITIES = [
    { key: 'spa' },
    { key: 'pool' },
    { key: 'seaFront' },
    { key: 'beach' },
    { key: 'fitness' },
    { key: 'parking' },
    { key: 'pets' },
    { key: 'ruStaff', ruOnly: true },
    { key: 'breakfast' },
    { key: 'family' },
];
const MAX_AMENITY_FACETS = 6;

const TIER_RANK = { budget: 0, mid: 1, upper: 2, luxury: 3 };
const areaKeyOf = (h) => h?.area?.en || h?.area?.ru || '';

/**
 * Страница «Все отели города» — в стиле Gallery·Field: герой, карта отелей,
 * фасетные фильтры (поиск + цена + звёзды + район + удобства + сортировка)
 * и сетка карточек, городской поиск кнопками.
 */
export default function AllHotelsPageContent({ city, lang = 'ru', affiliateProviders = [] }) {
    const isRu = lang === 'ru';
    const vp = t(lang, 'allHotelsPage');   // специфика страницы «Все отели города»

    const citySlug = city?.slug;
    const countrySlug = city?.countrySlug;
    const cityName = city?.name || '';                       // служебное имя (EN) — для поиска у партнёров
    const cityLabel = city?.cityLabel || city?.name || '';   // локализованное имя — для отображения
    const countryName = city?.countryLabel || city?.countryName || '';
    const countryCurrency = city?.countryCurrency;
    const cityCoords = city?.coords;
    const allHotels = useMemo(() => (city?.hotels || []).filter(Boolean), [city?.hotels]);

    // Данные для кнопок Trip.com (как на странице города)
    const affiliateProvider = (affiliateProviders || []).find((p) => p?.code === 'trip' && p?.active);
    const hasStayButton = !!(city?.stay?.tripCityId && city?.countryStay?.stay?.tripCountryId && affiliateProvider && countrySlug && citySlug);
    const cityData = { ...city, stay: city?.stay };
    const countryData = { ...city, stay: city?.countryStay?.stay };

    const [query, setQuery] = useState('');
    const [tiers, setTiers] = useState(() => new Set());      // multi-select цена
    const [starsSel, setStarsSel] = useState(() => new Set()); // multi-select звёзды
    const [area, setArea] = useState('all');                   // single район
    const [amenities, setAmenities] = useState(() => new Set()); // multi-select удобства (AND)
    const [sort, setSort] = useState('featured');

    const toggleSet = (setter) => (val) => setter((prev) => {
        const next = new Set(prev);
        if (next.has(val)) next.delete(val); else next.add(val);
        return next;
    });
    const toggleTier = toggleSet(setTiers);
    const toggleStar = toggleSet(setStarsSel);
    const toggleAmenity = toggleSet(setAmenities);

    // ── Доступные опции фасетов (только то, что реально встречается) ──────
    const tierCounts = useMemo(() => {
        const m = {};
        for (const h of allHotels) if (h.priceTier) m[h.priceTier] = (m[h.priceTier] || 0) + 1;
        return m;
    }, [allHotels]);

    const starOptions = useMemo(() => {
        const m = {};
        for (const h of allHotels) { const s = Number(h.stars); if (s) m[s] = (m[s] || 0) + 1; }
        return Object.keys(m).map(Number).sort((a, b) => b - a).map((s) => ({ value: s, count: m[s] }));
    }, [allHotels]);

    const areaOptions = useMemo(() => {
        const m = new Map();
        for (const h of allHotels) {
            const key = areaKeyOf(h);
            if (!key) continue;
            const label = (isRu ? h.area?.ru : h.area?.en) || key;
            const cur = m.get(key) || { value: key, label, count: 0 };
            cur.count += 1;
            m.set(key, cur);
        }
        return [...m.values()].sort((a, b) => a.label.localeCompare(b.label, isRu ? 'ru' : 'en'));
    }, [allHotels, isRu]);

    const amenityFacets = useMemo(() => {
        const total = allHotels.length;
        return FILTER_AMENITIES
            .filter((a) => isRu || !a.ruOnly) // «Рус. персонал» — только в русской версии
            .map((a) => ({ ...a, count: allHotels.reduce((n, h) => n + (h[a.key] ? 1 : 0), 0) }))
            .filter((a) => a.count > 0 && a.count < total) // отсекает «у всех» и «ни у кого»
            .slice(0, MAX_AMENITY_FACETS);
    }, [allHotels, isRu]);

    const priceTiersAvail = PRICE_TIERS.filter((t) => tierCounts[t.key]);
    const showPrice = priceTiersAvail.length > 1;
    const showStars = starOptions.length > 1;
    const showAmen = amenityFacets.length > 0;

    const hasActiveFilters = !!query.trim() || tiers.size > 0 || starsSel.size > 0 || area !== 'all' || amenities.size > 0;
    const resetFilters = () => { setQuery(''); setTiers(new Set()); setStarsSel(new Set()); setArea('all'); setAmenities(new Set()); };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = allHotels.filter((h) => {
            if (tiers.size && !tiers.has(h.priceTier)) return false;
            if (starsSel.size && !starsSel.has(Number(h.stars))) return false;
            if (area !== 'all' && areaKeyOf(h) !== area) return false;
            for (const key of amenities) if (!h[key]) return false; // AND
            if (q) {
                const hay = [h.name, h.area?.ru, h.area?.en, h.address].filter(Boolean).join(' ').toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });

        if (sort === 'featured') return list; // порядок из GROQ (featured → order → score)
        const sorted = [...list];
        if (sort === 'rating') sorted.sort((a, b) => (b.reviewScore ?? -1) - (a.reviewScore ?? -1));
        else if (sort === 'distance') sorted.sort((a, b) => (a.distCenterKm ?? Infinity) - (b.distCenterKm ?? Infinity));
        else if (sort === 'priceAsc') sorted.sort((a, b) => (TIER_RANK[a.priceTier] ?? 9) - (TIER_RANK[b.priceTier] ?? 9));
        else if (sort === 'priceDesc') sorted.sort((a, b) => (TIER_RANK[b.priceTier] ?? -1) - (TIER_RANK[a.priceTier] ?? -1));
        return sorted;
    }, [allHotels, query, tiers, starsSel, area, amenities, sort]);

    const heroRaw = city?.cover;
    const heroImage = unwrapImage(heroRaw);
    const heroBackground = heroImage
        ? urlFor(heroImage).width(1920).height(1080).fit('crop').url()
        : '/images/my-hero.jpg';

    const anchors = [
        cityCoords?.lat && { href: '#map', label: vp.anchorMap },
        { href: '#list', label: vp.anchorList },
    ].filter(Boolean);

    return (
        <main className="gf-root">
            {/* ── HERO ─────────────────────────────────────────── */}
            <section className="ct-hero">
                {heroBackground && <img src={heroBackground} alt={cityLabel} />}
                <div className="scrim" />
                <div className="ct-hero-in">
                    <h1>{vp.heroTitle(cityLabel)}</h1>
                    <p className="sub">{vp.heroSub}</p>
                </div>
            </section>

            {/* ── SUBNAV ───────────────────────────────────────── */}
            <GfSubnav lang={lang} leaf={vp.leaf} anchors={anchors} crumbs={[
                countrySlug && countryName && { href: `/${lang}/${countrySlug}`, label: countryName },
                countrySlug && { href: `/${lang}/${countrySlug}/cities`, label: vp.crumbCities },
                countrySlug && citySlug && { href: `/${lang}/${countrySlug}/${citySlug}`, label: cityLabel },
            ]} />

            {/* ── MAP ──────────────────────────────────────────── */}
            {cityCoords?.lat && cityCoords?.lng && (
                <section className="gf-sec" id="map">
                    <GfSectionMark eyebrow={vp.mapEyebrow(cityLabel)} title={vp.mapTitle} />
                    <p className="gf-lead">{vp.mapLead}</p>
                    <CityHotelsMap
                        hotels={filtered}
                        cityCoords={cityCoords}
                        cityName={cityLabel}
                        lang={lang}
                        countrySlug={countrySlug}
                        citySlug={citySlug}
                    />
                </section>
            )}

            {/* ── LIST ─────────────────────────────────────────── */}
            <section className="gf-sec" id="list" style={{ paddingTop: cityCoords?.lat ? 0 : undefined }}>
                <GfSectionMark
                    eyebrow={vp.listEyebrow(cityLabel)}
                    title={vp.listTitle}
                    tag={String(allHotels.length)}
                />
                <p className="gf-lead">{vp.listLead}</p>

                <div className="hf">
                    {/* поиск + район + сортировка — одна строка */}
                    <div className="hf-top">
                        <input
                            type="search"
                            className="hf-search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={vp.searchPlaceholder}
                            aria-label={vp.searchAria}
                        />
                        {areaOptions.length > 1 && (
                            <select className="hf-sel hf-area" value={area} onChange={(e) => setArea(e.target.value)}
                                aria-label={vp.areaAria}>
                                <option value="all">{vp.allAreas(allHotels.length)}</option>
                                {areaOptions.map((a) => (
                                    <option key={a.value} value={a.value}>{a.label} ({a.count})</option>
                                ))}
                            </select>
                        )}
                        <select className="hf-sel" value={sort} onChange={(e) => setSort(e.target.value)}
                            aria-label={vp.sortAria}>
                            <option value="featured">{vp.sort.featured}</option>
                            <option value="rating">{vp.sort.rating}</option>
                            <option value="distance">{vp.sort.distance}</option>
                            <option value="priceAsc">{vp.sort.priceAsc}</option>
                            <option value="priceDesc">{vp.sort.priceDesc}</option>
                        </select>
                    </div>

                    {/* чипы всех фасетов — одной строкой, без отдельных подписей */}
                    <div className="hf-row">
                        <div className="hf-chips">
                            {showPrice && priceTiersAvail.map((t) => (
                                <button key={t.key} type="button" aria-pressed={tiers.has(t.key)}
                                    title={vp.priceTitle}
                                    className={`hf-chip${tiers.has(t.key) ? ' is-active' : ''}`} onClick={() => toggleTier(t.key)}>
                                    {t.label}<span className="cnt">{tierCounts[t.key]}</span>
                                </button>
                            ))}

                            {showPrice && showStars && <span className="hf-div" aria-hidden="true" />}
                            {showStars && starOptions.map((s) => (
                                <button key={s.value} type="button" aria-pressed={starsSel.has(s.value)}
                                    title={vp.starsTitle}
                                    className={`hf-chip${starsSel.has(s.value) ? ' is-active' : ''}`} onClick={() => toggleStar(s.value)}>
                                    {s.value}<span className="hf-star">★</span><span className="cnt">{s.count}</span>
                                </button>
                            ))}

                            {(showPrice || showStars) && showAmen && <span className="hf-div" aria-hidden="true" />}
                            {showAmen && amenityFacets.map((a) => (
                                <button key={a.key} type="button" aria-pressed={amenities.has(a.key)}
                                    className={`hf-chip${amenities.has(a.key) ? ' is-active' : ''}`} onClick={() => toggleAmenity(a.key)}>
                                    {vp.amenity[a.key]}
                                </button>
                            ))}
                        </div>

                        <div className="hf-meta">
                            <span>{vp.countOf(filtered.length, allHotels.length)}</span>
                            {hasActiveFilters && (
                                <button type="button" className="hf-reset" onClick={resetFilters}>
                                    {vp.reset}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {filtered.length > 0 ? (
                    <div className="ct-hotels">
                        {filtered.map((h) => (
                            <HotelCard key={h._id} hotel={h} lang={lang} countrySlug={countrySlug} citySlug={citySlug} position="all-hotels" />
                        ))}
                    </div>
                ) : (
                    <div className="hf-empty">
                        <p>{vp.empty}</p>
                        <button type="button" className="gf-more" onClick={resetFilters}>
                            {vp.reset}
                        </button>
                    </div>
                )}

            </section>

            {/* ── CTA-баннер: ещё варианты жилья у партнёров ─────── */}
            {(cityName || hasStayButton) && (
                <GfCta
                    eyebrow={vp.ctaEyebrow}
                    title={vp.ctaTitle}
                >
                    <div className="gf-cta-btns">
                        {cityName && (
                            <BookingLinkButton
                                variant="bare"
                                className="gf-btn"
                                cityName={cityName}
                                countryName={countryName}
                                countryCurrency={countryCurrency}
                                citySlug={citySlug}
                                siteLang={lang}
                                subIdSuffix="all-hotels"
                            >
                                {vp.searchBooking}
                            </BookingLinkButton>
                        )}
                        {hasStayButton && (
                            <StayLinkButton
                                variant="bare"
                                className="gf-btn ghost"
                                countrySlug={countrySlug}
                                citySlug={citySlug}
                                siteLang={lang}
                                countryData={countryData}
                                cityData={cityData}
                                affiliateProvider={affiliateProvider}
                                linkParams={{ mode: 'base', subId: `${citySlug}-all-hotels` }}
                            >
                                {vp.searchTrip}
                            </StayLinkButton>
                        )}
                    </div>
                </GfCta>
            )}
        </main>
    );
}
